/**
 * A metadata packet, as a media_server sends it on the player's data channel: the agents'
 * detections and events, and the archive reader's warnings. nx::reflect writes each field and
 * omits an optional that is unset; it writes a NaN as null, which no number accepts.
 */

import { z } from 'zod';

import { listOf } from '@/api/listOf';

import { DeviceIdSchema } from './deviceId';

/** A box in the frame; each side is normalized to [0, 1]. */
const RectSchema = z.object({
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
});

/** What each agent packet carries. */
const MetadataBaseSchema = z.object({
    /** Its kind first, then a dot: `object.person`, `heatMap.motion`. */
    category: z.string(),
    deviceId: DeviceIdSchema,
    agentId: z.string(),
    utcTimeMs: z.number().int(),
    attributes: z.record(z.string(), z.string()),
});

const ObjectDetectionSchema = MetadataBaseSchema.extend({
    rect: RectSchema,
    /** The same for each detection of one physical object. */
    trackId: z.string(),
    /** 0 or absent when the agent gives none; older recordings have none. */
    confidence: z.number().optional(),
    /** When the box stops being valid; absent when the agent's frame rate is unknown. */
    utcEndTimeMs: z.number().int().optional(),
});
export type ObjectDetection = z.infer<typeof ObjectDetectionSchema>;

const HeatMapMetadataSchema = MetadataBaseSchema.extend({
    /** A bitfield of the frame's cells. */
    payload: z.base64(),
});
export type HeatMapMetadata = z.infer<typeof HeatMapMetadataSchema>;

/** A best shot. `data` is empty while the frame is still only a reference into the archive. */
const ImageSchema = z.object({
    mimeType: z.string(),
    data: z.base64(),
});

const EventMetadataSchema = MetadataBaseSchema.extend({
    trackId: z.string(),
    confidence: z.number().optional(),
    image: ImageSchema.optional(),
    /** The known object that the server matched the track to. */
    enrollment: z.object({
        id: z.string(),
        name: z.string(),
    }).optional(),
});

/** The key that no key held by the server matches, for the segment that plays. */
const DecryptKeyMissingSchema = z.object({
    /** Hex. */
    keyId: z.string(),
    /** Hex; empty when the archive has none. */
    salt: z.string(),
});
export type DecryptKeyMissing = z.infer<typeof DecryptKeyMissingSchema>;

/** The archive reader's warning; it is not agent metadata, so it has no agent fields. */
const InStreamMetadataSchema = z.object({
    category: z.string(),
    deviceId: DeviceIdSchema,
    eventType: z.literal('decryptKeyMissing'),
    payload: DecryptKeyMissingSchema,
});

const ObjectPacketSchema = z.object({
    metadataType: z.literal('object'),
    metadata: ObjectDetectionSchema,
});

const HeatMapPacketSchema = z.object({
    metadataType: z.literal('heatMap'),
    metadata: HeatMapMetadataSchema,
});

const EventPacketSchema = z.object({
    metadataType: z.literal('event'),
    metadata: EventMetadataSchema,
});

const BestShotPacketSchema = z.object({
    metadataType: z.literal('bestShot'),
    metadata: MetadataBaseSchema,
});

const InStreamPacketSchema = z.object({
    metadataType: z.literal('inStream'),
    metadata: InStreamMetadataSchema,
});

/** A packet of one kind; a list holds these, and never another list. */
export const ItemPacketSchema = z.discriminatedUnion('metadataType', [
    ObjectPacketSchema,
    HeatMapPacketSchema,
    EventPacketSchema,
    BestShotPacketSchema,
    InStreamPacketSchema,
]);
export type ItemPacket = z.infer<typeof ItemPacketSchema>;

/** All the metadata of one camera at one moment, such as the objects of one frame. */
const ListPacketSchema = z.object({
    metadataType: z.literal('list'),
    metadata: z.object({
        deviceId: DeviceIdSchema,
        utcTimeMs: z.number().int(),
        items: listOf(ItemPacketSchema, 'items'),
    }),
});

export const MetadataPacketSchema = z.discriminatedUnion('metadataType', [
    ...ItemPacketSchema.options,
    ListPacketSchema,
]);
export type MetadataPacket = z.infer<typeof MetadataPacketSchema>;
